import React from 'react';
import { SITE_STATUS, type SiteStatus } from '../../constants/siteStatus';
import { Badge } from '../ui/badge';
import {
  CheckCircle2,
  FileText,
  Hourglass,
  Loader2,
  PauseCircle,
  PowerOff,
  AlertTriangle,
  LucideIcon,
} from 'lucide-react';

interface SiteStatusBadgeProps {
  status: SiteStatus;
  className?: string;
  /** Optional error context surfaced on `failed` status as a tooltip. */
  lastError?: string | null;
  /**
   * When true, a `publishing` site has been stuck past the stall threshold.
   * Drops the pulse animation and renders the badge in the neutral "default"
   * variant so it visually reads as "waiting / stalled" rather than "active".
   */
  stalled?: boolean;
}

interface StatusConfig {
  variant: 'success' | 'destructive' | 'warning' | 'info' | 'default' | 'tertiary';
  icon: LucideIcon;
  label: string;
  spin?: boolean;
  /** Tailwind animation class applied to the badge background while in-flight. */
  pulseAnim?: string;
}

/**
 * Maps a site's lifecycle status to a badge variant + icon + label.
 *
 *   - Live:       success                 — deployed and serving traffic
 *   - Draft:      info (static)           — site exists, not yet published
 *   - Publishing: info + spinner + pulse  — fork/publish in flight (active)
 *   - Failed:     destructive             — most recent workflow failed
 *   - Suspended:  warning                 — subscription canceled
 *   - Destroying: destructive + spinner + pulse — destroy in flight
 *   - Offline:    default outlined        — torn down; content preserved
 */
const getStatusConfig = (status: SiteStatus): StatusConfig => {
  switch (status) {
    case SITE_STATUS.LIVE:
      return { variant: 'success', icon: CheckCircle2, label: 'Live' };
    case SITE_STATUS.DRAFT:
      return { variant: 'info', icon: FileText, label: 'Draft' };
    case SITE_STATUS.PUBLISHING:
      return {
        variant: 'info',
        icon: Loader2,
        label: 'Publishing',
        spin: true,
        pulseAnim: 'animate-badge-pulse-info',
      };
    case SITE_STATUS.FAILED:
      return { variant: 'destructive', icon: AlertTriangle, label: 'Failed' };
    case SITE_STATUS.SUSPENDED:
      return { variant: 'warning', icon: PauseCircle, label: 'Suspended' };
    case SITE_STATUS.DESTROYING:
      return {
        variant: 'destructive',
        icon: Loader2,
        label: 'Destroying',
        spin: true,
        pulseAnim: 'animate-badge-pulse-destructive',
      };
    case SITE_STATUS.OFFLINE:
      return { variant: 'tertiary', icon: PowerOff, label: 'Offline' };
    default:
      return { variant: 'default', icon: FileText, label: status };
  }
};

export const SiteStatusBadge: React.FC<SiteStatusBadgeProps> = ({
  status,
  className = '',
  lastError,
  stalled = false,
}) => {
  const baseConfig = getStatusConfig(status);
  // Stalled-publishing override: neutral gray, static icon (Hourglass — no
  // spin), label "Stalled", no pulse. Reads as "in-flight but no longer
  // making progress" rather than the active blue-pulsing publishing state.
  const config =
    stalled && status === SITE_STATUS.PUBLISHING
      ? {
          ...baseConfig,
          variant: 'default' as const,
          icon: Hourglass,
          label: 'Stalled',
          spin: false,
          pulseAnim: undefined,
        }
      : baseConfig;
  const Icon = config.icon;
  const title = status === SITE_STATUS.FAILED && lastError ? lastError : undefined;
  const badgeClass = [config.pulseAnim, className].filter(Boolean).join(' ');
  return (
    <Badge variant={config.variant} className={badgeClass} title={title}>
      <Icon className={`h-3 w-3 mr-1${config.spin ? ' animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

export default SiteStatusBadge;
